'use client';

import { useTranslations } from 'next-intl';
import { HStack, VStack } from '@terra/ui-styled-system';
import {
  ArrowDown01Icon,
  ArrowRight01Icon,
  Cancel01Icon,
} from '@terra/ui-icons';
import { AlertBanner } from '../../../components/alert-banner';
import { Button } from '../../../components/button';
import { Collapsible } from '../../../components/collapsible';
import { LegacyDialog } from '../../../components/dialog';
import { Field } from '../../../components/field';
import { IconButton } from '../../../components/icon-button';
import { Textarea } from '../../../components/textarea';
import {
  BodyDefault,
  BodyStrong,
  HeadingModal,
} from '../../../components/typography';

const DEFAULT_OPEN_REPRODUCE_STEPS_LIMIT = 4;

type FeatureOverrideRunDialogProps = {
  open: boolean;
  featureDescription: string;
  reproduceSteps: readonly string[];
  context: string;
  isSubmitting: boolean;
  onContextChange: (context: string) => void;
  onOpenChange: (open: boolean) => void;
  onSubmit: () => void;
};

export const FeatureOverrideRunDialog = ({
  open,
  featureDescription,
  reproduceSteps,
  context,
  isSubmitting,
  onContextChange,
  onOpenChange,
  onSubmit,
}: FeatureOverrideRunDialogProps) => {
  const t = useTranslations(
    'designSystem.applicationAssets.drawer.features.overrideRun',
  );
  const tCommon = useTranslations('designSystem.common');

  return (
    <LegacyDialog.Root
      open={open}
      onOpenChange={({ open: nextOpen }) => {
        if (!isSubmitting) {
          onOpenChange(nextOpen);
        }
      }}
      lazyMount
      unmountOnExit
    >
      <LegacyDialog.Backdrop />
      <LegacyDialog.Positioner>
        <LegacyDialog.Content maxW="xl" w="full">
          <VStack gap={4} p={6} alignItems="stretch">
            <VStack gap={2} alignItems="stretch">
              <LegacyDialog.Title asChild>
                <HeadingModal>
                  {t('title', { featureName: featureDescription })}
                </HeadingModal>
              </LegacyDialog.Title>
              <LegacyDialog.Description asChild>
                <BodyDefault color="fg.muted">{t('description')}</BodyDefault>
              </LegacyDialog.Description>
            </VStack>

            <AlertBanner
              kind="attention"
              description={t('warningDescription')}
            />

            {reproduceSteps.length > 0 && (
              <Collapsible.Root
                defaultOpen={
                  reproduceSteps.length <= DEFAULT_OPEN_REPRODUCE_STEPS_LIMIT
                }
              >
                <Collapsible.Trigger
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                  cursor="pointer"
                  color="fg.default"
                  mb={2}
                >
                  <Collapsible.Context>
                    {({ open }) =>
                      open ? (
                        <ArrowDown01Icon size={16} />
                      ) : (
                        <ArrowRight01Icon size={16} />
                      )
                    }
                  </Collapsible.Context>
                  <BodyStrong color="fg.default">
                    {t('reproduceSteps')}
                  </BodyStrong>
                </Collapsible.Trigger>
                <Collapsible.Content>
                  <VStack gap={1.5} alignItems="stretch">
                    {reproduceSteps.map((step, index) => (
                      <HStack
                        key={`${index}-${step}`}
                        gap={2}
                        alignItems="flex-start"
                      >
                        <BodyDefault color="fg.muted" flexShrink={0}>
                          {index + 1}.
                        </BodyDefault>
                        <BodyDefault color="fg.muted">{step}</BodyDefault>
                      </HStack>
                    ))}
                  </VStack>
                </Collapsible.Content>
              </Collapsible.Root>
            )}

            <Field.Root>
              <Field.Label>
                <BodyStrong color="fg.default">{t('contextLabel')}</BodyStrong>
              </Field.Label>
              <Textarea
                value={context}
                onChange={(event) => onContextChange(event.target.value)}
                placeholder={t('contextPlaceholder')}
                rows={2}
                disabled={isSubmitting}
                fontSize="xs"
                lineHeight="xs"
                _placeholder={{
                  fontSize: 'xs',
                  lineHeight: 'xs',
                  color: 'fg.muted',
                }}
              />
            </Field.Root>

            <HStack justifyContent="flex-end" gap={2}>
              <Button
                variant="outline"
                size="sm"
                disabled={isSubmitting}
                onClick={() => onOpenChange(false)}
              >
                {tCommon('cancel')}
              </Button>
              <Button
                size="sm"
                loading={isSubmitting}
                disabled={isSubmitting}
                onClick={onSubmit}
              >
                {t('submit')}
              </Button>
            </HStack>
          </VStack>
          <LegacyDialog.CloseTrigger
            asChild
            position="absolute"
            top="2"
            right="2"
          >
            <IconButton aria-label={tCommon('close')} variant="ghost" size="sm">
              <Cancel01Icon />
            </IconButton>
          </LegacyDialog.CloseTrigger>
        </LegacyDialog.Content>
      </LegacyDialog.Positioner>
    </LegacyDialog.Root>
  );
};
